import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import type { Locale } from 'date-fns'

import { Button } from '@/common/components/Button.tsx'
import { CalendarIcon } from '@/common/icons/general.tsx'
import { IconType } from '@/common/icons/types.ts'
import { cn } from '@/common/utilities.ts'
import { Calendar, CalendarProps } from '@/shad/ui/calendar.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/shad/ui/popover.tsx'
import React from 'react'
import { useTranslation } from 'react-i18next'

type Props = CalendarProps & {
  open: boolean
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>
  locale?: Locale
}

export function DatePicker(props: Props) {
  const { t: translate } = useTranslation()
  const { selected: date, open, onOpenChange, locale = enUS } = props

  // This only works for one date.
  // If you want to add support for multiple (start - end) dates here you'll have to handle an array of dates.
  // make sure you still support both single and multiple dates.
  const isDate = date instanceof Date

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground'
          )}
        >
          <CalendarIcon type={IconType.Outline} className="text-lg pr-4" />
          {isDate ? (
            format(date, 'PPP', { locale })
          ) : (
            <span>{translate('pick_a_date')}</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0">
        <Calendar {...props} />
      </PopoverContent>
    </Popover>
  )
}
